/**
 * Custom hook for buyer form management using React Hook Form
 * Unified hook for both create and edit operations
 */

import { useCallback } from 'react';
import { useForm } from 'react-hook-form';

import type { BuyerFormData, BuyerFormMode } from '../types';
import { buyerFormResolver } from '../validation/buyerFormValidation';

interface UseBuyerFormProps {
  /** Initial form data for edit mode */
  initialData?: Partial<BuyerFormData>;
  /** Form mode (create or edit) */
  mode: BuyerFormMode;
  /** Form submission handler */
  onSubmit: (data: BuyerFormData) => Promise<void>;
}

export const useBuyerForm = ({
  initialData,
  mode,
  onSubmit,
}: UseBuyerFormProps) => {
  const form = useForm<BuyerFormData>({
    mode: 'all',
    defaultValues: {
      name: '',
      displayName: '',
      fullAddress: '',
      deliveryComment: '',
      addressCity: '',
      addressCountry: '',
      isTest: false,
      addressLatitude: undefined,
      addressLongitude: undefined,
      addressUTCOffset: undefined,
      addressSource: 'custom',
      addressGooglePlaceId: undefined,
      addressGoogleAddressId: undefined,
      ...initialData,
    },
    resolver: buyerFormResolver,
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
    reset,
    setValue,
    watch,
  } = form;

  const handleFormSubmit = useCallback(
    async (data: BuyerFormData) => {
      await onSubmit(data);
      if (mode === 'create') {
        reset(); // Reset form after successful creation
      }
    },
    [onSubmit, mode, reset]
  );

  return {
    // Form methods
    register,
    handleSubmit: handleSubmit(handleFormSubmit),
    control,
    setValue,
    watch,
    reset,

    // Form state
    errors,
    isSubmitting,
    isDirty,

    // Computed values
    isEditMode: mode === 'edit',
  };
};
